import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { resilientFetch } from "./lib/resilientFetch";
import { PLATFORM, takePlatformToken } from "./platform";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;
const TOKEN_KEY = "hedaply_token";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Text for an API error body. FastAPI's `detail` is a string for most errors
// but a list of {loc, msg} objects for a 422 ("[object Object]" otherwise).
export function detailText(data, fallback) {
  const d = data && data.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && (x.msg || x.message)) || String(x)).join("; ");
  return d.message || d.msg || fallback;
}

export function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown / not logged in
  const [loading, setLoading] = useState(true);
  const [exchangeError, setExchangeError] = useState("");

  const fetchMe = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await resilientFetch(`${API}/auth/me`, { headers: authHeaders() });
      if (res.status === 401) {
        // Genuine auth failure — token revoked or expired. Sign out locally.
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      } else if (res.ok) {
        setUser(await res.json());
      } else {
        // 502 / 503 / 504 — transient origin error. Keep the token so the
        // user stays logged in once the backend is healthy again. Just
        // surface as "not logged in yet" for this render cycle.
        setUser(null);
      }
    } catch {
      // Hard network failure even after retries — keep the token, treat as
      // temporarily-offline so the user isn't kicked out by a WiFi blip.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Opened from the Heda Platform: trade its one-minute pass for our own
    // token (the same response a password login gets), then carry on.
    const pass = takePlatformToken();
    if (!pass) {
      fetchMe();
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/auth/platform-exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: pass }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(detailText(data, "Sign-in failed. Please try again."));
        localStorage.setItem(TOKEN_KEY, data.token);
        setUser(data.user);
      } catch (e) {
        // Never fall back to an older token: it may be someone else's.
        localStorage.removeItem(TOKEN_KEY);
        setExchangeError(e.message || "Sign-in failed. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(detailText(data, "Login failed"));
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: authHeaders(),
      });
    } catch {
      // ignore network errors — local sign-out still proceeds
    }
    localStorage.removeItem(TOKEN_KEY);
    if (PLATFORM) {
      window.location.assign("/"); // back to the platform's app launcher
      return;
    }
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh: fetchMe, exchangeError }}>
      {children}
    </AuthCtx.Provider>
  );
}
